import { createSlice } from "@reduxjs/toolkit";
import { DetailPackResponseType, TranscriptionBlockType } from "../../../utils/type";
import { closeFindReplace, fetchDetailPack, openFindReplace, setCurrentTimeValue, setAllMatches, setCurrentMatchIndex, setReplaceText, setSearchQuery, updateText, setPlaying, resetQCDetailPage, switchCC, switchW, openQcDetailDeleteModal, closeQcDetailDeleteModal, openEditSpeakerModal, closeEditSpeakerModal, updateSpeaker, updateBlock, openExportModal, closeExportModal, getTemplateOptions, exportTemplate, reopenForPreview, handleMergeBlock, handleMergeBlockSelect, clearMergeBlock, mergeBlock, deleteBlock, setEndTime, createNewSegment, setHighlightText, splitSegment, openQcDetailAudioBlockDeleteModal, closeQcDetailAudioBlockDeleteModal, setSelectedTranscriptionBlock, updateTime, openCompletedReviewModal, closeCompletedReviewModal, completeReview, replaceAll, addFindHistory, addReplaceHistory, emptyMergeBlock, resetHighlightText, setCurrentFocusBlockIndex } from "./QCDetailPageAction";

interface OptionType {
  label: string;
  value: any;
}

export interface ExportTemplateType {
  user_id: number;
  transcription_id: number;
  template_id: number;
  export_id: number;
}

export interface highlightedTextType {
  highlightText: string;
  type: "START" | "CENTER" | "END" | "FULL";
  remainingText: string[];
}
interface QCDetailPageStateType {
  loading: boolean;
  error: string | undefined;
  exportLoading: boolean;
  data: DetailPackResponseType | Record<string, any>;
  success: boolean;
  message: string;
  findHistory: string[];
  replaceHistory: string[];
  openFindReplaceState: boolean;
  searchQuery: string;
  replaceText: string;
  allMatches: any;
  currentMatchIndex: number;
  drmToken: any;
  mediaToken: any;
  currentTime: number;
  isPlaying: boolean;
  isCCActive: boolean;
  isFullWordActive: boolean;
  deleteModalState: boolean;
  deleteAudioBlockModalState: boolean;
  openEditSpeakerModal: boolean;
  updateSpeakerLoading: boolean;
  replaceAllLoading: boolean;
  openCompletedReviewModal: boolean;
  updateBlockLoading: boolean;
  currentFocusBlockIndex: number;
  exportModalState: boolean;
  openExportModal: boolean;
  templateOptions: OptionType[];
  templateOptionsLoading: boolean;
  completedReviewLoading: boolean;
  downloadLoading: boolean;
  exportTemplateState: ExportTemplateType;
  mergeBlockLoading: boolean;
  splitSegmentLoading: boolean;
  createNewSegmentLoading: boolean;
  updateTimeLoading: boolean;
  deleteLoading: boolean;
  mergeBlockId: number[];
  endTime: number;
  highlightedText: highlightedTextType;
  selectedTranscriptionBlock: number;
}

const initialState: QCDetailPageStateType = {
  loading: true,
  exportLoading: false,
  error: "",
  //Replace this data default value with the default value like idPack should be 0 and string should be empty string
  data: {},
  exportModalState: false,
  downloadLoading: false,
  exportTemplateState: {
    user_id: 0,
    transcription_id: 0,
    template_id: 0,
    export_id: 0,
  },
  findHistory: [],
  replaceHistory: [],
  replaceAllLoading: false,
  searchQuery: "",
  replaceText: "",
  success: false,
  message: "",
  openFindReplaceState: false,
  allMatches: [],
  currentMatchIndex: 0,
  drmToken: {},
  mediaToken: {},
  currentTime: 0,
  isPlaying: false,
  isCCActive: false,
  isFullWordActive: false,
  deleteModalState: false,
  deleteAudioBlockModalState: false,
  openEditSpeakerModal: false,
  openCompletedReviewModal: false,
  updateSpeakerLoading: false,
  updateBlockLoading: false,
  currentFocusBlockIndex: 0,
  splitSegmentLoading: false,
  mergeBlockLoading: false,
  createNewSegmentLoading: false,
  deleteLoading: false,
  updateTimeLoading: false,
  completedReviewLoading: false,
  mergeBlockId: [],
  endTime: 0,
  highlightedText: {
    highlightText: "",
    type: "START",
    remainingText: [],
  },
  selectedTranscriptionBlock: 0,
  openExportModal: false,
  templateOptions: [],
  templateOptionsLoading: false,
};
/**
 * Redux slice for managing the state of the QC Detail Page.
 *
 * This slice handles various actions related to the QC Detail Page, including:
 * - Fetching and updating transcription data.
 * - Managing the state of UI elements such as modals and find/replace functionality.
 * - Handling operations like merging, splitting, deleting, and creating transcription blocks.
 * - Managing playback controls and search/replace operations.
 *
 * State Properties:
 * - `loading`: Indicates whether data is being fetched.
 * - `data`: Holds the transcription data and related metadata.
 * - `mediaToken`: Token for accessing media resources.
 * - `drmToken`: Token for DRM-protected resources.
 * - `success`: Indicates whether the last operation was successful.
 * - `error`: Holds error messages for failed operations.
 * - `openFindReplaceState`: Tracks the visibility of the find/replace UI.
 * - `searchQuery`: Stores the current search query.
 * - `replaceText`: Stores the current replacement text.
 * - `allMatches`: Stores all matches found for the search query.
 * - `currentMatchIndex`: Tracks the index of the currently selected match.
 * - `currentTime`: Tracks the current playback time.
 * - `isPlaying`: Indicates whether media playback is active.
 * - `isCCActive`: Tracks the state of closed captions.
 * - `isFullWordActive`: Tracks the state of full-word search functionality.
 * - `deleteModalState`: Tracks the visibility of the delete confirmation modal.
 * - `updateSpeakerLoading`: Indicates whether a speaker update operation is in progress.
 * - `updateBlockLoading`: Indicates whether a block update operation is in progress.
 * - `mergeBlockId`: Stores the IDs of blocks selected for merging.
 * - `mergeBlockLoading`: Indicates whether a merge operation is in progress.
 * - `deleteLoading`: Indicates whether a delete operation is in progress.
 * - `splitSegmentLoading`: Indicates whether a split operation is in progress.
 * - `createNewSegmentLoading`: Indicates whether a new segment creation operation is in progress.
 * - `highlightedText`: Stores the currently highlighted text.
 * - `endTime`: Tracks the end time of a segment.
 * - `openEditSpeakerModal`: Tracks the visibility of the edit speaker modal.
 *
 * Actions Handled:
 * - `fetchDetailPack`: Handles fetching of transcription data.
 * - `updateText`: Updates transcription blocks with new text.
 * - `openFindReplace` / `closeFindReplace`: Toggles the find/replace UI.
 * - `setSearchQuery`: Updates the search query.
 * - `setReplaceText`: Updates the replacement text.
 * - `setAllMatches`: Updates the list of all matches for the search query.
 * - `setCurrentMatchIndex`: Updates the index of the current match.
 * - `setCurrentTimeValue`: Updates the current playback time.
 * - `setPlaying`: Toggles playback state.
 * - `switchCC`: Toggles closed captions.
 * - `switchW`: Toggles full-word search functionality.
 * - `resetQCDetailPage`: Resets the state to its initial values.
 * - `openQcDetailDeleteModal` / `closeQcDetailDeleteModal`: Toggles the delete confirmation modal.
 * - `updateSpeaker`: Handles updating speaker information.
 * - `updateBlock`: Handles updating transcription blocks.
 * - `handleMergeBlock`: Handles selecting blocks for merging.
 * - `clearMergeBlock`: Clears the selection of blocks for merging.
 * - `handleMergeBlockSelect`: Handles selecting additional blocks for merging.
 * - `mergeBlock`: Handles merging of selected blocks.
 * - `deleteBlock`: Handles deletion of transcription blocks.
 * - `splitSegment`: Handles splitting of transcription blocks.
 * - `createNewSegment`: Handles creation of new transcription blocks.
 * - `setHighlightText`: Updates the highlighted text.
 * - `setEndTime`: Updates the end time of a segment.
 * - `openEditSpeakerModal` / `closeEditSpeakerModal`: Toggles the edit speaker modal.
 */
const QCDetailPageSlice = createSlice({
  name: "QCDetailPage",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchDetailPack.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchDetailPack.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload.data;
      state.mediaToken = action.payload.mediaToken;
      state.drmToken = action.payload.drmToken;

      state.success = true;
    });
    builder.addCase(fetchDetailPack.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
    builder.addCase(updateText, (state, action) => {
      const { updatedText } = action.payload;
      state.data.transcriptions[0].transcriptionBlocks = updatedText;
    });
    builder.addCase(reopenForPreview.fulfilled, (state, action) => {
      state.data = action.payload.data;
    });

    builder.addCase(openFindReplace, (state) => {
      state.openFindReplaceState = true;
    });
    builder.addCase(closeFindReplace, (state) => {
      state.openFindReplaceState = false;
    });
    builder.addCase(setSearchQuery, (state, action) => {
      state.searchQuery = action.payload.searchQuery;
    });
    builder.addCase(setReplaceText, (state, action) => {
      state.replaceText = action.payload.replaceText;
    });
    builder.addCase(setAllMatches, (state, action) => {
      state.allMatches = action.payload.allMatches;
    });
    builder.addCase(setCurrentMatchIndex, (state, action) => {
      state.currentMatchIndex = action.payload.currentMatchIndex;
    });
    builder.addCase(setCurrentTimeValue, (state, action) => {
      state.currentTime = action.payload.time;
    });
    builder.addCase(setPlaying, (state, action) => {
      state.isPlaying = action.payload.playing;
    });
    builder.addCase(switchCC, (state, action) => {
      state.isCCActive = !state.isCCActive;
    });
    builder.addCase(switchW, (state, action) => {
      state.isFullWordActive = !state.isFullWordActive;
    });
    builder
      .addCase(resetQCDetailPage, (state) => {
        return {
          ...initialState,
        };
      })
      .addCase(openQcDetailDeleteModal, (state, action) => {
        state.deleteModalState = true;
      })
      .addCase(closeQcDetailDeleteModal, (state, action) => {
        state.deleteModalState = false;
      })

      .addCase(openQcDetailAudioBlockDeleteModal, (state, action) => {
        state.deleteAudioBlockModalState = true;
      })
      .addCase(closeQcDetailAudioBlockDeleteModal, (state, action) => {
        state.deleteAudioBlockModalState = false;
      });

    builder.addCase(updateSpeaker.pending, (state, action) => {
      state.updateSpeakerLoading = true;
    });
    builder.addCase(updateSpeaker.fulfilled, (state, action) => {
      state.updateSpeakerLoading = false;
      state.data.transcriptions[0].transcriptionBlocks = action.payload.transcriptionBlocks;
      state.data.transcriptions[0].speakers = action.payload.speakers;
    });
    builder.addCase(updateSpeaker.rejected, (state, action) => {
      state.updateSpeakerLoading = false;
    });
    builder.addCase(updateTime.pending, (state, action) => {
      state.updateTimeLoading = true;
    });
    builder.addCase(updateTime.fulfilled, (state, action) => {
      state.updateTimeLoading = false;
      state.data.transcriptions[0].transcriptionBlocks = action.payload.transcriptionBlocks;
      state.data.transcriptions[0].speakers = action.payload.speakers;
    });
    builder.addCase(updateTime.rejected, (state, action) => {
      state.updateTimeLoading = false;
    });

    builder.addCase(updateBlock.pending, (state, action) => {
      // state.updateSpeakerLoading = true;
      state.updateBlockLoading = true;
    });
    builder.addCase(updateBlock.fulfilled, (state, action) => {
      // state.updateSpeakerLoading = false;
      state.updateBlockLoading = false;
      state.data.transcriptions[0].transcriptionBlocks[action.payload.blockIndex].transcript = action.payload.transcriptionBlocks[action.payload.blockIndex].transcript;
      state.data.transcriptions[0].transcriptionBlocks[action.payload.blockIndex].speaker = action.payload.transcriptionBlocks[action.payload.blockIndex].speaker;
      state.data.transcriptions[0].speakers = action.payload.speakers;
    });
    builder.addCase(updateBlock.rejected, (state, action) => {
      // state.updateSpeakerLoading = false;
      state.updateBlockLoading = false;
    });

    builder.addCase(handleMergeBlock, (state, action) => {
      const { blockIndex } = action.payload;
      const blocks = state.data.transcriptions[0].transcriptionBlocks;
      state.mergeBlockId = [...state.mergeBlockId, blocks[blockIndex].idTranscriptionBlock];
      if (blockIndex < 0 || blockIndex >= blocks.length - 1) {
        blocks[blockIndex] = {
          ...blocks[blockIndex],
          isSelected: true,
          showed: true,
        };
      } else {
        blocks[blockIndex] = {
          ...blocks[blockIndex],
          isSelected: true,
          showed: true,
        };

        blocks[blockIndex + 1] = {
          ...blocks[blockIndex + 1],
          isSelected: false,
          showed: true,
        };
      } // Prevent out-of-bounds error
    });
    builder.addCase(emptyMergeBlock, (state) => {
      state.mergeBlockId = [];
    });
    builder.addCase(clearMergeBlock, (state) => {
      state.mergeBlockId = [];
      state.data.transcriptions[0].transcriptionBlocks = state.data.transcriptions[0].transcriptionBlocks.map((block: TranscriptionBlockType) => {
        return {
          ...block,
          isSelected: false,
          showed: false,
        };
      });
    });
    builder.addCase(resetHighlightText, (state) => {
      state.highlightedText = {
        highlightText: "",
        type: "START",
        remainingText: [],
      };
    });
    builder.addCase(handleMergeBlockSelect, (state, action) => {
      const { blockIndex } = action.payload;
      const blocks = state.data.transcriptions[0].transcriptionBlocks;
      const blockId = blocks[blockIndex].idTranscriptionBlock;
      const idPos = state.mergeBlockId.indexOf(blockId);

      if (idPos !== -1) {
        // If already selected, remove this block and all after it
        const newMergeBlockId = state.mergeBlockId.slice(0, idPos);
        const remainingBlockId = state.mergeBlockId.slice(idPos + 1, state.mergeBlockId.length);
        state.mergeBlockId = newMergeBlockId;
        if (blockIndex + 1 >= blocks.length) {
          return;
        }

        // Reset the next block to not selected and not showed
        for (let i = blockIndex + 1; i < blocks.length; i++) {
          blocks[i] = {
            ...blocks[i],
            isSelected: false,
            showed: false,
          };
        }
        return;
        //I think this condition is not correct, because if the block is in the middle of mergeBlockId, it should not remove all after this block but it just check the block length
      }

      state.mergeBlockId = [...state.mergeBlockId, blockId];

      if (blockIndex + 1 >= blocks.length) {
        return;
      }

      blocks[blockIndex + 1] = {
        ...blocks[blockIndex + 1],
        isSelected: false,
        showed: true,
      };
    });
    builder.addCase(mergeBlock.pending, (state) => {
      state.mergeBlockLoading = true;
    });
    builder.addCase(mergeBlock.fulfilled, (state, action) => {
      state.mergeBlockLoading = false;
      state.data.transcriptions[0].transcriptionBlocks = action.payload.transcriptionBlocks;
      state.data.transcriptions[0].speakers = action.payload.speakers;
    });
    builder.addCase(mergeBlock.rejected, (state) => {
      state.mergeBlockLoading = false;
    });

    //For deleting the segment
    builder.addCase(deleteBlock.pending, (state, action) => {
      state.deleteLoading = true;
    });
    builder.addCase(deleteBlock.fulfilled, (state, action) => {
      state.deleteLoading = false;
      state.data.transcriptions[0].transcriptionBlocks = action.payload.transcriptionBlocks;
      state.data.transcriptions[0].speakers = action.payload.speakers;
    });
    builder.addCase(deleteBlock.rejected, (state) => {
      state.deleteLoading = false;
    });

    //For splitting the segment
    builder.addCase(splitSegment.pending, (state) => {
      state.splitSegmentLoading = true;
    });
    builder.addCase(splitSegment.fulfilled, (state, action) => {
      state.splitSegmentLoading = false;
      state.data.transcriptions[0].transcriptionBlocks = action.payload.transcriptionBlocks;
      state.data.transcriptions[0].speakers = action.payload.speakers;
    });
    builder.addCase(splitSegment.rejected, (state) => {
      state.splitSegmentLoading = false;
    });
    builder.addCase(createNewSegment.pending, (state) => {
      state.createNewSegmentLoading = true;
    });
    builder.addCase(createNewSegment.fulfilled, (state, action) => {
      state.createNewSegmentLoading = false;
       const prevBlocks = state.data.transcriptions[0].transcriptionBlocks;
      const newBlocks = action.payload.transcriptionBlocks;

      // Preserve isSelected and showed for existing blocks
      state.data.transcriptions[0].transcriptionBlocks = newBlocks.map((newBlock: any) => {
        const prevBlock = prevBlocks.find((b: any) => b.idTranscriptionBlock === newBlock.idTranscriptionBlock);
        return prevBlock
          ? { ...newBlock, isSelected: prevBlock.isSelected, showed: prevBlock.showed }
          : newBlock;
      });
     
      state.data.transcriptions[0].speakers = action.payload.speakers;
    });
    builder.addCase(createNewSegment.rejected, (state) => {
      state.createNewSegmentLoading = false;
    });

    builder.addCase(completeReview.pending, (state) => {
      state.completedReviewLoading = true;
    });
    builder.addCase(completeReview.fulfilled, (state, action) => {
      state.completedReviewLoading = false;
      state.data = action.payload;
    });
    builder.addCase(completeReview.rejected, (state) => {
      state.completedReviewLoading = false;
    });
    builder.addCase(replaceAll.pending, (state) => {
      state.replaceAllLoading = true;
    });
    builder.addCase(replaceAll.fulfilled, (state, action) => {
      state.replaceAllLoading = false;
      state.data.transcriptions[0].transcriptionBlocks = action.payload.transcriptionBlocks;
      state.data.transcriptions[0].speakers = action.payload.speakers;
    });
    builder.addCase(replaceAll.rejected, (state) => {
      state.replaceAllLoading = false;
    });
    builder.addCase(setHighlightText, (state, action) => {
      state.highlightedText = action.payload.highlightText;
    });

    builder.addCase(addFindHistory, (state, action) => {
      const { history } = action.payload;
      state.findHistory = [history, ...state.findHistory];
    });
    builder.addCase(addReplaceHistory, (state, action) => {
      const { history } = action.payload;
      state.replaceHistory = [history, ...state.replaceHistory];
    });
    builder.addCase(setEndTime, (state, action) => {
      state.endTime = action.payload.endTime;
    });

    builder.addCase(setCurrentFocusBlockIndex, (state, action) => {
      state.currentFocusBlockIndex = action.payload.currentFocusBlockIndex;
    });
    builder.addCase(openEditSpeakerModal, (state) => {
      state.openEditSpeakerModal = true;
    });
    builder.addCase(closeEditSpeakerModal, (state) => {
      state.openEditSpeakerModal = false;
    });
    builder.addCase(setSelectedTranscriptionBlock, (state, action) => {
      state.selectedTranscriptionBlock = action.payload;
    });
    builder.addCase(openExportModal, (state, action) => {
      state.exportModalState = true;
    });
    builder.addCase(closeExportModal, (state, action) => {
      state.exportModalState = false;
    });
    builder.addCase(openCompletedReviewModal, (state, action) => {
      state.openCompletedReviewModal = true;
    });
    builder.addCase(closeCompletedReviewModal, (state, action) => {
      state.openCompletedReviewModal = false;
    });

    //Get Template Options
    builder.addCase(getTemplateOptions.pending, (state, action) => {
      state.templateOptionsLoading = true;
    });
    builder.addCase(getTemplateOptions.fulfilled, (state, action) => {
      state.templateOptions = action.payload;
      state.templateOptionsLoading = false;
    });
    builder.addCase(getTemplateOptions.rejected, (state, action) => {
      state.error = action.error.message;
      state.templateOptionsLoading = false;
    });
    //Export Template
    builder.addCase(exportTemplate.pending, (state, action) => {
      state.exportLoading = true;
    });
    builder.addCase(exportTemplate.fulfilled, (state, action) => {
      state.exportLoading = false;
    });
    builder.addCase(exportTemplate.rejected, (state, action) => {
      state.error = action.error.message;
      state.exportLoading = false;
    });
  },
});

export default QCDetailPageSlice.reducer;
